import { capitalize } from 'HELPERS/text';
import { hasMultipleValues, hasSomeValues } from 'HELPERS/array';
import { convertToPixel, genereateStyleObj } from 'HELPERS/formatter';
import { isNumber, validHex, validHtml } from 'HELPERS/validator';


const displayKeyMap = {
  preferabli_logo: 'use_preferabli_logo',
  placement_powered_by_winering: 'logo_placement',
  powered_by_placement: 'logo_placement',
  powered_by_logo_option: 'logo_option',
  logo_class: 'logoClass',
  logo_inline_style: 'logoInlineStyle',
  logo_before: 'logoBefore',
  logo_after: 'logoAfter',
  seperator: 'separator'
};

const defaultDisplayValues = {
  logo_placement: 'top-right',
  logo_option: 9,
  logoClass: '',
  logoInlineStyle: {},
  layout: 'row'
};

const transformLogoPlacement = (placement) => {
  if (placement === 'top') return 'top-right';
  if (placement === 'bottom') return 'bottom-right';
  return placement;
};

const validateHtmlOrThrow = (key, value) => {
  if (!validHtml(value)) throw `Display option:${key}, requires to be a valid html string.`;
  return value;
};

const transformKeys = (obj, map) => {
  for (const [oldKey, newKey] of Object.entries(map)) {
    if (oldKey in obj) {
      obj[newKey] = obj[oldKey];
      delete obj[oldKey];
    }
  }
};

function normalizeSeparatorShow(separator) {
  const showKeys = Object.keys(separator).filter(k => ['top', 'bottom', 'both'].includes(k));
  if (showKeys.includes('top') && showKeys.includes('bottom')) {
    separator.show = 'both';
  } else if (separator.top) separator.show = 'top';
  else if (separator.bottom) separator.show = 'bottom';
  else if (separator.both) separator.show = 'both';

  ['top', 'bottom', 'both'].forEach(k => delete separator[k]);
}

function normalizeSeparatorStyle(separator) {
  const style = separator.style || {};
  const borders = ['top', 'bottom'];

  borders.forEach(pos => {
    if (!style[pos]) return;

    const section = style[pos];
    const inline = [];

    if (hasMultipleValues(Object.keys(section), ['border_width', 'border_color', 'border_style'])) {
      const values = [
        convertToPixel(section.border_width),
        section.border_style,
        section.border_color
      ];

      if (!validHex(section.border_color)) throw `${capitalize(pos)} border color is invalid.`;

      style[`border${capitalize(pos)}`] = {
        style: values.join(' '),
        inline: []
      };
    } else {
      const inlineObj = {};
      if (section.border_width) inlineObj[`border${capitalize(pos)}Width`] = convertToPixel(section.border_width);
      if (section.border_color) {
        if (!validHex(section.border_color)) throw `${capitalize(pos)} border color is invalid.`;
        inlineObj[`border${capitalize(pos)}Color`] = section.border_color;
      }
      if (section.border_style) inlineObj[`border${capitalize(pos)}Style`] = section.border_style;

      style[`border${capitalize(pos)}`] = {
        style: null,
        inline: [inlineObj]
      };
    }

    if (section.spacing_top) style[`border${capitalize(pos)}`].inline.push({ marginTop: section.spacing_top });
    if (section.spacing_bottom) style[`border${capitalize(pos)}`].inline.push({ marginBottom: section.spacing_bottom });

    delete style[pos];
  });

  if (
    Object.keys(style).some(k => ['border_width', 'border_color', 'border_style', 'spacing_top', 'spacing_bottom'].includes(k))
  ) {
    const tmp = {};

    if (hasMultipleValues(Object.keys(style), ['border_width', 'border_color', 'border_style'])) {
      const combined = [];
      if (style.border_width) combined.push(convertToPixel(style.border_width));
      if (style.border_style) combined.push(style.border_style);
      if (style.border_color) {
        if (!validHex(style.border_color)) throw `Border color is invalid.`;
        combined.push(style.border_color);
      }

      tmp.border = combined.join(' ');
    } else {
      tmp.borderTop = { inline: [], style: null };

      ['border_width', 'border_color', 'border_style'].forEach(prop => {
        if (!style[prop]) return;
        const val = style[prop];

        switch (prop) {
          case 'border_width':
            tmp.borderTop.inline.push({ borderTopWidth: convertToPixel(val) });
            break;
          case 'border_color':
            if (!validHex(val)) throw `Border color is invalid.`;
            tmp.borderTop.inline.push({ borderTopColor: val });
            break;
          case 'border_style':
            tmp.borderTop.inline.push({ borderTopStyle: val });
            break;
        }
      });
    }

    if (style.spacing_top) tmp.spacingTop = style.spacing_top;
    if (style.spacing_bottom) tmp.spacingBottom = style.spacing_bottom;

    Object.assign(style, tmp);
  }

  separator.style = style;

  // Sanity check for top/bottom conflicts
  if (['top', 'bottom'].includes(separator.show)) {
    const keys = Object.keys(separator.style).filter(k => ['border', 'borderTop', 'borderBottom'].includes(k));
    if (keys.length > 1) {
      throw new Error(`Only one border style allowed when show is "${separator.show}".`);
    }
  }
}

const normalizeOptions = (options) => {
  if ('showViewButton' in options) {
    options.showLink = options.showViewButton;
    delete options.showViewButton;
  }

  const display = options?.display;

  transformKeys(display, displayKeyMap);

  // Defaults
  display.logoInlineStyle = (typeof display.logoInlineStyle === 'string') ? genereateStyleObj(display.logoInlineStyle) : defaultDisplayValues.logoInlineStyle;
  display.logo_placement = transformLogoPlacement(display.logo_placement || defaultDisplayValues.logo_placement);
  display.logo_option ??= defaultDisplayValues.logo_option;
  display.logoClass = display.logoClass || defaultDisplayValues.logoClass;
  display.layout ??= defaultDisplayValues.layout;

  // Conditional overrides
  if (display.use_preferabli_logo && !('logo_option' in display)) {
    display.logo_option = 4;
  }

  // Layout warning
  if (display.layout === 'list') {
    display.powered_by_placement = 'bottom';
    if (options.renderProductCards) {
      delete options.renderProductCards;
      throw 'Unable to use renderProductCards with this layout mode. Use row or column instead.';
    }
  }

  // Validate logo HTML content
  if (display.logoBefore) display.logoBefore = validateHtmlOrThrow('logo_before', display.logoBefore);
  if (display.logoAfter) display.logoAfter = validateHtmlOrThrow('logo_after', display.logoAfter);

  // Separator cleanup
  if ('separator' in display) {
    display.separator = { ...display.separator };
    normalizeSeparatorShow(display.separator);
    normalizeSeparatorStyle(display.separator);
  }


  return options;
}

export default normalizeOptions;